<template>
  <span
    class="download-icon-wrapper"
    :class="{
      'download-icon-wrapper--downloading': isDownloading,
      'download-icon-wrapper--queued': isQueued,
      'download-icon-wrapper--downloaded': isDownloaded,
      'download-icon-wrapper--paused': showsAsPaused,
      'download-icon-wrapper--disabled': disabled
    }"
    :style="{ color: iconColor }"
    :title="tooltip"
    @click.stop="!disabled && $emit('click')"
  >
    <!-- Disco de fundo. É um elemento próprio com `opacity` em vez de
         uma cor derivada (lighten/rgba) porque a primária é escolhida pelo
         produtor e pode vir em qualquer formato — hex, rgb(), hsl(), nome. Fazer
         a conta exigiria parsear todos eles; o disco só repassa a string pro CSS.
         `color-mix()` resolveria sozinho, mas é Safari 16.2+ e o app roda em
         WebView a partir do iOS 15.6. -->
    <span v-if="showsDisc" class="download-disc" :style="{ background: iconColor }"></span>
    <!-- Anel só quando há progresso pra comunicar. Nos demais estados o ícone
         fica solto, sem círculo em volta: o download é ação secundária e o
         círculo permanente dava a ele peso de item principal na linha.
         2π × r(10) ≈ 62.83 — atualizar stroke-dasharray se r mudar. -->
    <svg v-if="showsRing" class="download-ring" viewBox="0 0 24 24">
      <circle
        class="download-ring__progress"
        cx="12" cy="12" r="10"
        stroke-dasharray="62.83"
        :stroke-dashoffset="dashoffset"
        stroke-linecap="round"
        :style="{ stroke: ringColor }"
      />
    </svg>
    <span v-if="showsPercent" class="download-percent" :style="{ fontSize: percentFontSize + 'px' }">
      {{ percentLabel }}
    </span>
    <i v-else class="material-icons">{{ iconName }}</i>
  </span>
</template>

<script>
// EM ANDAMENTO usa a cor do tema:
// é ação em curso, não estado terminal — e assim acompanha os botões da página
// da aula, que também são do tema.
const COLOR_DONE = '#2fca6e';
const COLOR_PAUSED = '#f9a825';

export default {
  name: 'DownloadIcon',
  props: {
    status: {
      type: String,
      default: 'idle' // 'idle' | 'queued' | 'downloading' | 'downloaded'
    },
    progress: {
      type: Number,
      default: null // null = preenchimento instantâneo (LO), número = baseado em progresso (aula/módulo)
    },
    // True quando o LO tem download interrompido por falha de rede com arquivos
    // parciais preservados no disco. Visual: cor âmbar + ícone "continuar".
    // Clique dispara o mesmo handler (download retoma de onde parou via
    // reconcileExistingFiles do plugin nativo).
    hasPartial: {
      type: Boolean,
      default: false
    },
    // Desabilita interação (típico: app offline e LO ainda não baixado).
    // Visual: ícone esmaecido + cursor not-allowed + click bloqueado.
    // Quem decide a condição é o pai — esse componente só reflete.
    disabled: {
      type: Boolean,
      default: false
    },
    isModule: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    isDownloaded () { return this.status === 'downloaded' },
    isDownloading () { return this.status === 'downloading' },
    isQueued () { return this.status === 'queued' },
    isActive () { return this.isDownloaded || this.isDownloading },
    // O disco nasceu só pro concluído, mas ele é o que dá corpo ao ícone na
    // lista: sem ele, as linhas em andamento ficam com um glifo solto e não se
    // lêem como "este item já tem algo acontecendo". Estende pra downloading e
    // queued — os três estados em que o LO está comprometido com o offline.
    // Fora deles (idle, pausado) o ícone segue sem fundo, porque ali ele é
    // convite pra ação, não indicação de estado.
    showsDisc () { return this.isDownloaded || this.isDownloading || this.isQueued },
    // Estado "pausado" só aparece quando o LO está idle (não baixando agora) e
    // tem parcial salvo. Durante o downloading/queued ativo, ignoramos o hasPartial.
    showsAsPaused () { return !this.isActive && !this.isQueued && this.hasPartial },
    // Durante a transferência o número dentro do anel diz mais que o ícone: o anel
    // sozinho é difícil de ler em 24px, ainda mais nos primeiros %. Só no
    // 'downloading' — 'queued' não tem progresso e o estado pausado precisa manter
    // o play_arrow, que é a affordance de "toque para continuar".
    showsPercent () {
      return this.isDownloading && typeof this.progress === 'number'
    },
    percentLabel () {
      return String(Math.max(0, Math.min(100, Math.round(this.progress))))
    },
    // O diâmetro útil dentro do anel é ~24px (r=10 no viewBox de 24, escalado pra
    // 32px, menos o stroke). "100%" em Montserrat a 11px passa disso; a 9px cabe.
    // Encolher só nos 3 dígitos mantém o número legível nos outros 99% do tempo,
    // em vez de fixar o tamanho do pior caso pra sempre.
    percentFontSize () {
      return this.percentLabel.length > 2 ? 9 : 11
    },
    showsRing () {
      // Concluído usa o disco preenchido — anel por cima dele viraria duas
      // circunferências concêntricas dizendo a mesma coisa.
      if (this.isDownloaded) return false;
      if (this.isDownloading || this.showsAsPaused) return true;
      // Turma parcialmente baixada (ex.: 3 de 9 aulas): o status agregado ainda
      // é 'idle', e sem o anel o ícone fica idêntico ao de uma turma em que nada
      // foi baixado — some a única pista de que já há conteúdo no disco.
      return typeof this.progress === 'number' && this.progress > 0;
    },
    iconName () {
      if (this.isDownloaded) return 'mobile_friendly'
      if (this.isQueued) return 'schedule'
      if (this.showsAsPaused) return 'play_arrow'
      return 'save_alt'
    },
    iconColor () {
      // Desabilitado sobrescreve qualquer outro estado — visual cinza claro
      // pra deixar claro que não dá pra interagir, mesmo se tiver parcial.
      if (this.disabled) return this.$theme?.darkTheme ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.18)'
      // if (this.isDownloaded) return COLOR_DONE
      if (this.showsAsPaused) return COLOR_PAUSED
      // Em andamento / na fila: cor do curso — o número da % fica dentro do
      // anel, então os dois precisam ser a mesma cor pra ler como um elemento.
      if (this.isDownloaded || this.isDownloading || this.isQueued || (this.isModule && this.showsRing)) return this.$theme?.primaryColor
      return this.$theme?.darkTheme ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.25)'
    },
    ringColor () {
      if (this.showsAsPaused) return COLOR_PAUSED
      return this.$theme?.primaryColor
    },
    tooltip () {
      if (this.disabled) return 'Sem conexão — conecte-se para baixar'
      if (this.isQueued) return 'Na fila — toque para cancelar'
      if (this.showsAsPaused) return 'Conexão perdida — toque para continuar'
      if (this.isDownloading) {
        return this.showsPercent
          ? `Baixando ${this.percentLabel}% — toque para cancelar`
          : 'Baixando — toque para cancelar'
      }
      if (this.isDownloaded) return 'Disponível offline'
      return 'Baixar para uso offline'
    },
    dashoffset () {
      if (this.isDownloaded) return 0
      if (this.isQueued) return 62.83 // anel vazio enquanto aguarda a vez
      if (this.showsAsPaused) return 62.83 * (1 - (this.progress || 0) / 100)
      if (this.progress !== null) return 62.83 * (1 - this.progress / 100)
      // Sem número de progresso (ex: turma baixando com 0 LOs concluídos →
      // getClassDownloadedRatio retorna null): ring vazio. O fallback antigo
      // era 0 (ring cheio) e fazia parecer 100% mesmo com download em curso.
      return 62.83
    }
  }
}
</script>

<style scoped lang="scss">
.download-icon-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    // 32px em TODOS os estados, inclusive os que só mostram ícone: o número de
    // progresso precisa de ~24px úteis dentro do anel, e reservar o espaço sempre
    // evita a linha da lista pular de altura quando o download começa e termina.
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    cursor: pointer;

    // Baixando: clicável (cancela o download). Antes era not-allowed porque o
    // clique era bloqueado no template — não havia como desistir de um download.
    &--downloading {
        cursor: pointer;
        pointer-events: auto;
    }

    // Desabilitado por contexto (ex: offline). Cursor not-allowed + sem hover
    // scale + sem pulso. Mantém pointer-events auto pro tooltip funcionar.
    &--disabled {
        cursor: not-allowed;
        pointer-events: auto;
        animation: none;

        @media (hover: hover) and (pointer: fine) {
            &:hover {
                transform: none;
            }
        }
    }

    // Pulso sutil pra chamar atenção que tem ação pendente (continuar download)
    &--paused {
        animation: download-icon-pulse 2s ease-in-out infinite;
    }

    // Na fila: clicável (cancela e tira da fila) + pulso pra sinalizar "aguardando vez".
    &--queued {
        cursor: pointer;
        pointer-events: auto;
        animation: download-icon-pulse 2s ease-in-out infinite;
    }
    user-select: none;
    transition: color 0.2s ease, transform 0.15s ease;

    // `and (pointer: fine)` exclui touch (pointer: coarse), onde o :hover fica
    // "preso" após o tap e pintaria o ícone concluído de vermelho.
    @media (hover: hover) and (pointer: fine) {
        &:hover {
            transform: scale(1.1);
        }

        &--downloaded:hover {
            color: #ff5252 !important;
        }
    }

    .material-icons {
        font-size: 18px;
        position: relative;
        z-index: 1;
        line-height: 1;
    }

    // font-size vem do componente (percentFontSize) — muda com o nº de dígitos.
    // Herda a cor do wrapper.
    .download-percent {
        position: relative;
        z-index: 1;
        display: inline-flex;
        align-items: baseline;
        font-weight: 700;
        line-height: 1;
        letter-spacing: -0.3px;
        white-space: nowrap;
        // Sem isto o número "pula" de posição a cada tick (1 → 10 → 100).
        font-variant-numeric: tabular-nums;
        font-feature-settings: 'tnum';

        // O "%" é o glifo mais largo do conjunto (~0.95em em Montserrat). Menor
        // que o número, devolve ~3px — a diferença entre "100%" caber e vazar.
        // Em `em` pra acompanhar o tamanho adaptativo sem uma segunda regra.
        &__sign {
            font-size: 0.7em;
            margin-left: 0.5px;
        }
    }

    // A MESMA cor do glifo, só que rebaixada por opacity. Fica atrás dele
    // (z-index abaixo), então o glifo permanece na cor cheia e legível — se a
    // opacity fosse no wrapper, ela apagaria os dois juntos.
    .download-disc {
        position: absolute;
        inset: 0;
        border-radius: 50%;
        opacity: 0.18;
        pointer-events: none;
    }

    .download-ring {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        transform: rotate(-90deg);


        &__progress {
            fill: none;
            stroke: #2fca6e;
            stroke-width: 2;
            transition: stroke-dashoffset 0.4s ease, stroke 0.2s ease;
        }
    }
}

@keyframes download-icon-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.55; }
}
</style>
